import React from 'react';
import { FiCloud, FiChevronDown, FiChevronUp, FiSave, FiTrash2, FiMoreVertical, FiPlus, FiArrowUp, FiArrowDown, FiSliders, FiShare2, FiUsers } from 'react-icons/fi';
import PublishDialog from './PublishDialog';
import { fetchPlanPublicationStatus, fetchPlanVersions, fetchStudentReleaseStatus, setStudentRelease } from '../api';

const MeinePlaene = ({
  user,
  plans,
  loading,
  error,
  refresh,
  expandedPlans,
  setExpandedPlans,
  planVersions,
  setPlanVersions,
  loadingVersions,
  setLoadingVersions,
  deleteDropdownOpen,
  setDeleteDropdownOpen,
  showEditButtons,
  onLoadFromDatabase,
  onUpdatePlan,
  handleDeletePlan,
  handleDeleteAllVersions,
  handleDeleteOlderVersions,
  loadPlanVersions,
  togglePlanExpansion,
  toggleDeleteDropdown,
  onCreateNewPlan,
  currentLoadedPlanId,
  isInTab = false // Neue Prop
}) => {
  const [sortBy, setSortBy] = React.useState('date'); // 'date' oder 'name'
  const [sortOrder, setSortOrder] = React.useState('desc'); // 'asc' oder 'desc'
  const [showSortDropdown, setShowSortDropdown] = React.useState(false);
  const [publishDialog, setPublishDialog] = React.useState({ open: false, planId: null, planName: null });
  const [publicationStatus, setPublicationStatus] = React.useState({}); // planId -> boolean
  const [basePlanPublicationStatus, setBasePlanPublicationStatus] = React.useState({}); // baseName -> boolean (any version published)
  const [studentReleaseStatus, setStudentReleaseStatus] = React.useState({}); // planId -> boolean

  // Sortiere die Pläne basierend auf aktuellen Einstellungen
  const sortedPlans = React.useMemo(() => {
    if (!plans || plans.length === 0) return [];
    
    const sorted = [...plans].sort((a, b) => {
      if (sortBy === 'name') {
        const comparison = a.name.localeCompare(b.name, 'de');
        return sortOrder === 'asc' ? comparison : -comparison;
      } else {
        const dateA = new Date(a.updated_at);
        const dateB = new Date(b.updated_at);
        return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
      }
    });
    
    return sorted;
  }, [plans, sortBy, sortOrder]);

  // Veröffentlichungs-Status für alle sichtbaren Pläne laden
  const loadPublicationStatus = React.useCallback(async () => {
    if (!user?.username || !plans || plans.length === 0) return;
    
    const statusPromises = [];
    const planIds = new Set();
    
    // Sammle alle Plan-IDs (Haupt-Pläne + Versionen)
    plans.forEach(plan => {
      planIds.add(plan.id);
      const versions = planVersions[plan.base_name] || [];
      versions.forEach(version => planIds.add(version.id));
    });
    
    // Lade Status für alle Plan-IDs
    for (const planId of planIds) {
      statusPromises.push(
        fetchPlanPublicationStatus(planId, user.username).then(result => ({
          planId,
          isPublished: result.success && result.publishedGroups && result.publishedGroups.length > 0
        }))
      );
    }
    
    try {
      const results = await Promise.all(statusPromises);
      const newStatus = {};
      results.forEach(({ planId, isPublished }) => {
        newStatus[planId] = isPublished;
      });
      setPublicationStatus(prev => ({ ...prev, ...newStatus }));
    } catch (error) {
      console.error('Fehler beim Laden des Veröffentlichungs-Status:', error);
    }
  }, [user?.username, plans, planVersions]);

  // Lernenden-Freigabe-Status für alle sichtbaren Pläne laden
  const loadStudentReleaseStatus = React.useCallback(async () => {
    if (!user?.username || !plans || plans.length === 0) return;
    
    const statusPromises = [];
    const planIds = new Set();
    
    // Sammle alle Plan-IDs (Haupt-Pläne + Versionen)
    plans.forEach(plan => {
      planIds.add(plan.id);
      const versions = planVersions[plan.base_name] || [];
      versions.forEach(version => planIds.add(version.id));
    });
    
    // Lade Status für alle Plan-IDs
    for (const planId of planIds) {
      statusPromises.push(
        fetchStudentReleaseStatus(planId, user.username).then(result => ({
          planId,
          isReleased: result.success && result.studentReleased
        }))
      );
    }
    
    try {
      const results = await Promise.all(statusPromises);
      const newStatus = {};
      results.forEach(({ planId, isReleased }) => {
        newStatus[planId] = isReleased;
      });
      setStudentReleaseStatus(prev => ({ ...prev, ...newStatus }));
    } catch (error) {
      console.error('Fehler beim Laden des Lernenden-Freigabe-Status:', error);
    }
  }, [user?.username, plans, planVersions]);

  // Funktion um alle Versionen eines Plans und deren Status zu laden
  const loadAllVersionsPublicationStatus = React.useCallback(async (baseName) => {
    if (!user?.username) return false;
    
    try {
      // Lade alle Versionen für diesen baseName
      const versionsResult = await fetchPlanVersions(user.username, baseName);
      
      if (versionsResult.error || !versionsResult.success || !versionsResult.versions) {
        return false;
      }
      
      // Prüfe Status aller Versionen
      const statusPromises = versionsResult.versions.map(version => 
        fetchPlanPublicationStatus(version.id, user.username).then(result => ({
          planId: version.id,
          isPublished: result.success && result.publishedGroups && result.publishedGroups.length > 0
        }))
      );
      
      const results = await Promise.all(statusPromises);
      let hasAnyPublished = false;
      const newStatus = {};
      
      results.forEach(({ planId, isPublished }) => {
        newStatus[planId] = isPublished;
        if (isPublished) hasAnyPublished = true;
      });
      
      // Update Status
      setPublicationStatus(prev => ({ ...prev, ...newStatus }));
      setBasePlanPublicationStatus(prev => ({ ...prev, [baseName]: hasAnyPublished }));
      
      return hasAnyPublished;
    } catch (error) {
      console.error('Fehler beim Laden aller Versionen Status:', error);
      return false;
    }
  }, [user?.username]);

  // Separater Effect für das initiale Laden aller Plan-Status
  React.useEffect(() => {
    if (!user?.username || !plans || plans.length === 0) return;
    
    const loadAllPlansStatus = async () => {
      // Für jeden Plan: Lade Status aller Versionen
      const promises = plans.map(plan => loadAllVersionsPublicationStatus(plan.base_name));
      
      try {
        await Promise.all(promises);
      } catch (error) {
        console.error('Fehler beim Laden aller Plan-Status:', error);
      }
    };
    
    loadAllPlansStatus();
  }, [user?.username, plans, loadAllVersionsPublicationStatus]);

  // Hilfsfunktion: Prüft ob irgendeine Version eines Plans veröffentlicht ist
  const isAnyVersionPublished = (baseName) => {
    // Verwende den cached Status falls verfügbar
    if (basePlanPublicationStatus.hasOwnProperty(baseName)) {
      return basePlanPublicationStatus[baseName];
    }
    
    // Fallback: Prüfe nur die aktuell geladenen Daten
    const mainPlan = plans.find(p => p.base_name === baseName);
    const versions = planVersions[baseName] || [];
    
    // Prüfe Haupt-Plan
    if (mainPlan && publicationStatus[mainPlan.id]) {
      return true;
    }
    
    // Prüfe alle geladenen Versionen
    return versions.some(version => publicationStatus[version.id]);
  };



  // Handler für Student Release Button
  const handleStudentReleaseClick = async (planId, currentStatus) => {
    try {
      const result = await setStudentRelease(planId, !currentStatus, user.username);
      
      if (result.success) {
        // Status lokal aktualisieren
        setStudentReleaseStatus(prev => ({
          ...prev,
          [planId]: !currentStatus
        }));
        
        // Auch die Plans refreshen um aktualisierte Daten zu bekommen
        if (window.refreshPlans) {
          window.refreshPlans();
        }
      } else {
        console.error('Fehler beim Ändern der Lernenden-Freigabe:', result.message);
        // Hier könnte eine Fehlermeldung angezeigt werden
      }
    } catch (error) {
      console.error('Fehler beim Ändern der Lernenden-Freigabe:', error);
    }
  };

  // Status laden wenn Pläne oder Versionen sich ändern
  React.useEffect(() => {
    loadPublicationStatus();
    loadStudentReleaseStatus();
  }, [loadPublicationStatus, loadStudentReleaseStatus]);

  // Schließe Dropdown bei Klick außerhalb
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSortDropdown && !event.target.closest('.sort-dropdown-container')) {
        setShowSortDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSortDropdown]);

  if (!user) return null;
  return (
    <div style={{ 
      padding: isInTab ? '12px' : '12px', 
      borderTop: isInTab ? 'none' : '1px solid #ddd', 
      marginTop: isInTab ? '0' : '16px',
      backgroundColor: isInTab ? 'transparent' : '#fafafa',
      position: 'relative',
      overflow: 'visible'
    }}>

      {!isInTab && (
        <h4 style={{ 
          margin: '0 0 12px 0', 
          fontSize: '16px',
          color: '#333',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FiCloud size={16} />
            Meine Pläne
            
            {/* Sortier-Dropdown */}
            <div className="sort-dropdown-container" style={{ position: 'relative' }}>
              <button
                onClick={() => setShowSortDropdown(!showSortDropdown)}
                style={{
                  background: 'none',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  color: '#666',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '12px',
                  backgroundColor: showSortDropdown ? '#f5f5f5' : 'transparent'
                }}
                title="Sortierung ändern"
              >
                <FiSliders size={12} />
              </button>
              
              {showSortDropdown && (
                <div 
                  ref={(el) => {
                    if (el && el.isConnected) {
                      try {
                        const rect = el.getBoundingClientRect();
                        const viewportHeight = window.innerHeight;
                        const spaceBelow = viewportHeight - rect.top;
                        const dropdownHeight = el.scrollHeight;
                        
                        if (spaceBelow < dropdownHeight + 20) {
                          // Nicht genug Platz unten, nach oben öffnen
                          el.style.top = 'auto';
                          el.style.bottom = '100%';
                          el.style.marginTop = '0';
                          el.style.marginBottom = '2px';
                        } else {
                          // Genug Platz unten
                          el.style.top = '100%';
                          el.style.bottom = 'auto';
                          el.style.marginTop = '2px';
                          el.style.marginBottom = '0';
                        }
                      } catch (error) {
                        console.warn('Error positioning dropdown:', error);
                      }
                    }
                  }}
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    backgroundColor: 'white',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    zIndex: 10000,
                    minWidth: '180px',
                    marginTop: '2px'
                  }}>
                  {/* Nach Name sortieren */}
                  <button
                    onClick={() => {
                      setSortBy('name');
                      setSortOrder('asc');
                      setShowSortDropdown(false);
                    }}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: 'none',
                      backgroundColor: sortBy === 'name' && sortOrder === 'asc' ? '#e3f2fd' : 'transparent',
                      color: '#333',
                      cursor: 'pointer',
                      fontSize: '12px',
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      borderBottom: '1px solid #f0f0f0'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = sortBy === 'name' && sortOrder === 'asc' ? '#e3f2fd' : '#f5f5f5'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = sortBy === 'name' && sortOrder === 'asc' ? '#e3f2fd' : 'transparent'}
                  >
                    <FiArrowUp size={12} />
                    Name A → Z
                  </button>
                  
                  <button
                    onClick={() => {
                      setSortBy('name');
                      setSortOrder('desc');
                      setShowSortDropdown(false);
                    }}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: 'none',
                      backgroundColor: sortBy === 'name' && sortOrder === 'desc' ? '#e3f2fd' : 'transparent',
                      color: '#333',
                      cursor: 'pointer',
                      fontSize: '12px',
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      borderBottom: '1px solid #f0f0f0'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = sortBy === 'name' && sortOrder === 'desc' ? '#e3f2fd' : '#f5f5f5'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = sortBy === 'name' && sortOrder === 'desc' ? '#e3f2fd' : 'transparent'}
                  >
                    <FiArrowDown size={12} />
                    Name Z → A
                  </button>
                  
                  {/* Nach Datum sortieren */}
                  <button
                    onClick={() => {
                      setSortBy('date');
                      setSortOrder('desc');
                      setShowSortDropdown(false);
                    }}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: 'none',
                      backgroundColor: sortBy === 'date' && sortOrder === 'desc' ? '#e3f2fd' : 'transparent',
                      color: '#333',
                      cursor: 'pointer',
                      fontSize: '12px',
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      borderBottom: '1px solid #f0f0f0'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = sortBy === 'date' && sortOrder === 'desc' ? '#e3f2fd' : '#f5f5f5'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = sortBy === 'date' && sortOrder === 'desc' ? '#e3f2fd' : 'transparent'}
                  >
                    <FiArrowDown size={12} />
                    Neueste zuerst
                  </button>
                  
                  <button
                    onClick={() => {
                      setSortBy('date');
                      setSortOrder('asc');
                      setShowSortDropdown(false);
                    }}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: 'none',
                      backgroundColor: sortBy === 'date' && sortOrder === 'asc' ? '#e3f2fd' : 'transparent',
                      color: '#333',
                      cursor: 'pointer',
                      fontSize: '12px',
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = sortBy === 'date' && sortOrder === 'asc' ? '#e3f2fd' : '#f5f5f5'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = sortBy === 'date' && sortOrder === 'asc' ? '#e3f2fd' : 'transparent'}
                  >
                    <FiArrowUp size={12} />
                    Älteste zuerst
                  </button>
                </div>
              )}
            </div>
          </div>
          {showEditButtons && (
            <button
              onClick={onCreateNewPlan}
              style={{
                background: 'none',
                border: '1px solid #4caf50',
                borderRadius: '4px',
                color: '#4caf50',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                fontSize: '12px'
              }}
              title="Neuen Plan erstellen"
            >
              <FiPlus size={14} />
            </button>
          )}
        </h4>
      )}
      
      {/* Toolbar für Tab-Ansicht */}
      {isInTab && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px 0',
          marginBottom: '12px',
          borderBottom: '1px solid #eee'
        }}>
          {/* Sortier-Dropdown */}
          <div className="sort-dropdown-container" style={{ position: 'relative' }}>
            <button
              onClick={() => setShowSortDropdown(!showSortDropdown)}
              style={{
                background: 'none',
                border: '1px solid #ddd',
                borderRadius: '4px',
                color: '#666',
                cursor: 'pointer',
                padding: '6px 10px',
                display: 'flex',
                alignItems: 'center',
                fontSize: '12px',
                backgroundColor: showSortDropdown ? '#f5f5f5' : 'transparent',
                gap: '6px'
              }}
              title="Sortierung ändern"
            >
              <FiSliders size={12} />
              Sortieren
            </button>
            
            {showSortDropdown && (
              <div 
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  backgroundColor: 'white',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  zIndex: 10000,
                  minWidth: '180px',
                  marginTop: '2px'
                }}>
                <button
                  onClick={() => {
                    setSortBy('name');
                    setSortOrder('asc');
                    setShowSortDropdown(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: 'none',
                    backgroundColor: sortBy === 'name' && sortOrder === 'asc' ? '#e3f2fd' : 'transparent',
                    color: '#333',
                    cursor: 'pointer',
                    fontSize: '12px',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    borderBottom: '1px solid #f0f0f0'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = sortBy === 'name' && sortOrder === 'asc' ? '#e3f2fd' : '#f5f5f5'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = sortBy === 'name' && sortOrder === 'asc' ? '#e3f2fd' : 'transparent'}
                >
                  <FiArrowUp size={12} />
                  Name A → Z
                </button>
                
                <button
                  onClick={() => {
                    setSortBy('name');
                    setSortOrder('desc');
                    setShowSortDropdown(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: 'none',
                    backgroundColor: sortBy === 'name' && sortOrder === 'desc' ? '#e3f2fd' : 'transparent',
                    color: '#333',
                    cursor: 'pointer',
                    fontSize: '12px',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    borderBottom: '1px solid #f0f0f0'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = sortBy === 'name' && sortOrder === 'desc' ? '#e3f2fd' : '#f5f5f5'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = sortBy === 'name' && sortOrder === 'desc' ? '#e3f2fd' : 'transparent'}
                >
                  <FiArrowDown size={12} />
                  Name Z → A
                </button>
                
                <button
                  onClick={() => {
                    setSortBy('date');
                    setSortOrder('desc');
                    setShowSortDropdown(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: 'none',
                    backgroundColor: sortBy === 'date' && sortOrder === 'desc' ? '#e3f2fd' : 'transparent',
                    color: '#333',
                    cursor: 'pointer',
                    fontSize: '12px',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    borderBottom: '1px solid #f0f0f0'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = sortBy === 'date' && sortOrder === 'desc' ? '#e3f2fd' : '#f5f5f5'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = sortBy === 'date' && sortOrder === 'desc' ? '#e3f2fd' : 'transparent'}
                >
                  <FiArrowDown size={12} />
                  Neueste zuerst
                </button>
                
                <button
                  onClick={() => {
                    setSortBy('date');
                    setSortOrder('asc');
                    setShowSortDropdown(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: 'none',
                    backgroundColor: sortBy === 'date' && sortOrder === 'asc' ? '#e3f2fd' : 'transparent',
                    color: '#333',
                    cursor: 'pointer',
                    fontSize: '12px',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = sortBy === 'date' && sortOrder === 'asc' ? '#e3f2fd' : '#f5f5f5'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = sortBy === 'date' && sortOrder === 'asc' ? '#e3f2fd' : 'transparent'}
                >
                  <FiArrowUp size={12} />
                  Älteste zuerst
                </button>
              </div>
            )}
          </div>
          
          {/* Neuen Plan Button für Tab-Ansicht */}
          {showEditButtons && (
            <button
              onClick={onCreateNewPlan}
              style={{
                background: '#4caf50',
                border: 'none',
                borderRadius: '4px',
                color: 'white',
                cursor: 'pointer',
                padding: '6px 10px',
                display: 'flex',
                alignItems: 'center',
                fontSize: '12px',
                gap: '4px'
              }}
              title="Neuen Plan erstellen"
            >
              <FiPlus size={12} />
              Neu
            </button>
          )}
        </div>
      )}
      {loading && (
        <div style={{ 
          textAlign: 'center', 
          padding: '20px 0', 
          color: '#666',
          fontSize: '14px'
        }}>
          Lade Pläne...
        </div>
      )}
      {error && (
        <div style={{ 
          textAlign: 'center', 
          padding: '20px 0', 
          color: '#f44336',
          fontSize: '14px'
        }}>
          Fehler: {error}
          <button
            onClick={refresh}
            style={{
              marginLeft: '8px',
              padding: '4px 8px',
              border: '1px solid #f44336',
              backgroundColor: 'white',
              color: '#f44336',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            Erneut versuchen
          </button>
        </div>
      )}
      {!loading && !error && plans.length === 0 && (
        <div style={{ 
          textAlign: 'center', 
          padding: '20px 0', 
          color: '#666',
          fontSize: '14px'
        }}>
          Noch keine Pläne gespeichert.
        </div>
      )}
      {!loading && !error && plans.length > 0 && (
        <div style={{ 
          minHeight: isInTab ? '300px' : '400px', 
          maxHeight: isInTab ? '400px' : '600px', 
          overflowY: 'auto', 
          overflowX: 'visible', 
          position: 'relative' 
        }}>
          {sortedPlans.map(plan => {
            const isExpanded = expandedPlans.has(plan.base_name);
            const versions = planVersions[plan.base_name] || [];
            const hasMultipleVersions = versions.length > 1 || plan.version > 1;
            const isLoadingVersions = loadingVersions.has(plan.base_name);
            const isCurrentlyLoaded = currentLoadedPlanId === plan.id;
            return (
              <div key={plan.id} style={{
                marginBottom: '8px',
                border: isCurrentlyLoaded ? '2px solid #2196f3' : '1px solid #e0e0e0',
                borderRadius: '6px',
                backgroundColor: 'white',
                boxShadow: isCurrentlyLoaded ? '0 0 12px rgba(33, 150, 243, 0.4)' : 'none',
                animation: isCurrentlyLoaded ? 'planGlow 2s ease-in-out infinite' : 'none',
                position: 'relative',
                overflow: 'visible'
              }}>
                {/* Haupt-Plan */}
                <div style={{
                  padding: '8px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      marginBottom: '2px'
                    }}>
                      <div style={{
                        fontWeight: '500',
                        fontSize: '14px',
                        color: '#333',
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        onLoadFromDatabase(plan.id);
                      }}
                      title="Klicken zum Laden"
                      >
                        {plan.name}
                      </div>
                      {hasMultipleVersions && (
                        <button
                          onClick={() => togglePlanExpansion(plan.base_name)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#666',
                            padding: '2px',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                          title={`${isExpanded ? 'Versionen ausblenden' : 'Versionen anzeigen'}`}
                        >
                          {isLoadingVersions ? (
                            <div style={{
                              width: '12px',
                              height: '12px',
                              border: '2px solid #ddd',
                              borderTop: '2px solid #666',
                              borderRadius: '50%',
                              animation: 'spin 1s linear infinite'
                            }} />
                          ) : isExpanded ? (
                            <FiChevronUp size={12} />
                          ) : (
                            <FiChevronDown size={12} />
                          )}
                        </button>
                      )}
                      <div style={{
                        fontSize: '11px',
                        color: '#999',
                        backgroundColor: '#f0f0f0',
                        padding: '2px 6px',
                        borderRadius: '10px'
                      }}>
                        v{plan.version}
                      </div>
                    </div>
                    {plan.description && (
                      <div style={{
                        fontSize: '12px',
                        color: '#666',
                        marginBottom: '4px'
                      }}>
                        {plan.description}
                      </div>
                    )}
                    <div style={{
                      fontSize: '11px',
                      color: '#999'
                    }}>
                      {new Date(plan.updated_at).toLocaleDateString('de-DE', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  </div>
                  <div style={{
                    display: 'flex',
                    gap: '4px',
                    marginLeft: '8px'
                  }}>
                    {/* Publish Button */}
                    {showEditButtons && (
                      <button
                        onClick={() => {
                          if (hasMultipleVersions) {
                            // Bei mehreren Versionen: Versionen-Liste öffnen
                            togglePlanExpansion(plan.base_name);
                          } else {
                            // Bei nur einer Version: Dialog direkt öffnen
                            setPublishDialog({ 
                              open: true, 
                              planId: plan.id, 
                              planName: plan.name 
                            });
                          }
                        }}
                        style={{
                          padding: '4px',
                          border: 'none',
                          backgroundColor: isAnyVersionPublished(plan.base_name) ? '#2196f3' : '#adacacff',
                          color: 'white',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                        title={hasMultipleVersions 
                          ? (isAnyVersionPublished(plan.base_name) 
                             ? 'es gibt Veröffentlichungen: Verwaltung öffnen' 
                             : 'Veröffentlichungen verwalten')
                          : (isAnyVersionPublished(plan.base_name) 
                             ? 'Veröffentlichung verwalten' 
                             : 'Plan für Gruppen veröffentlichen')}
                      >
                        <FiShare2 size={12} />
                      </button>
                    )}
                    {/* Student Release Button */}
                    {showEditButtons && (
                      <button
                        onClick={() => handleStudentReleaseClick(plan.id, studentReleaseStatus[plan.id])}
                        style={{
                          padding: '4px',
                          border: 'none',
                          backgroundColor: studentReleaseStatus[plan.id] ? '#4caf50' : '#adacacff',
                          color: 'white',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                        title={studentReleaseStatus[plan.id] 
                          ? 'Plan ist für Lernende freigegeben (Klick zum Sperren)' 
                          : 'Plan für Lernende freigeben'}
                      >
                        <FiUsers size={12} />
                      </button>
                    )}
                    {/* Update Button - nur bei aktivem Plan */}
                    {showEditButtons && isCurrentlyLoaded && (
                      <button
                        onClick={() => onUpdatePlan(plan.id, plan.name, plan.description)}
                        style={{
                          padding: '4px',
                          border: 'none',
                          backgroundColor: '#ff9800',
                          color: 'white',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                        title="Speichere den aktuellen Plan als neue Version unter diesem Namen."
                      >
                        <FiSave size={12} />
                      </button>
                    )}
                    {/* Delete Dropdown */}
                    {showEditButtons && (
                      <div className="delete-dropdown-container" style={{ position: 'relative' }}>
                        <button
                          onClick={() => toggleDeleteDropdown(plan.base_name)}
                          style={{
                            padding: '4px',
                            border: 'none',
                            backgroundColor: '#f44336',
                            color: 'white',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '2px',
                            height: '24px'
                          }}
                          title="Löschen-Optionen"
                        >
                          <FiTrash2 size={12} />
                          <FiMoreVertical size={8} />
                        </button>
                        {deleteDropdownOpen === plan.base_name && (
                          <div 
                            ref={(el) => {
                              if (el && el.isConnected) {
                                try {
                                  const rect = el.getBoundingClientRect();
                                  const container = el.closest('[style*="maxHeight: 600px"]');
                                  if (container) {
                                    const containerRect = container.getBoundingClientRect();
                                    const spaceBelow = containerRect.bottom - rect.top;
                                    const dropdownHeight = el.scrollHeight;
                                    
                                    if (spaceBelow < dropdownHeight + 20) {
                                      // Nicht genug Platz unten, nach oben öffnen
                                      el.style.top = 'auto';
                                      el.style.bottom = '100%';
                                      el.style.marginTop = '0';
                                      el.style.marginBottom = '2px';
                                    } else {
                                      // Genug Platz unten
                                      el.style.top = '100%';
                                      el.style.bottom = 'auto';
                                      el.style.marginTop = '2px';
                                      el.style.marginBottom = '0';
                                    }
                                  }
                                } catch (error) {
                                  console.warn('Error positioning delete dropdown:', error);
                                }
                              }
                            }}
                            style={{
                              position: 'absolute',
                              top: '100%',
                              right: 0,
                              backgroundColor: 'white',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                              zIndex: 10000,
                              minWidth: '220px',
                              marginTop: '2px'
                            }}>
                            {/* Aktuelle Version löschen - immer anzeigen */}
                            <button
                              onClick={() => handleDeletePlan(plan.id, plan.name)}
                              style={{
                                width: '100%',
                                padding: '8px 12px',
                                border: 'none',
                                backgroundColor: 'transparent',
                                color: '#d32f2f',
                                cursor: 'pointer',
                                fontSize: '12px',
                                textAlign: 'left',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                borderBottom: (planVersions[plan.base_name]?.length > 1 || plan.version > 1) ? '1px solid #f0f0f0' : 'none',
                                fontWeight: (planVersions[plan.base_name]?.length > 1 || plan.version > 1) ? 'normal' : 'bold'
                              }}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#ffebee'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                              title={`Löscht nur Version ${plan.version}`}
                            >
                              <FiTrash2 size={12} />
                              {(planVersions[plan.base_name]?.length > 1 || plan.version > 1) 
                                ? `Diese Version löschen (v${plan.version})` 
                                : 'Plan löschen'}
                            </button>
                            
                            {/* Nur aktuelle Version behalten - nur bei mehreren Versionen */}
                            {(planVersions[plan.base_name]?.length > 1 || plan.version > 1) && (
                              <button
                                onClick={() => handleDeleteOlderVersions(plan.base_name, plan.id)}
                                style={{
                                  width: '100%',
                                  padding: '8px 12px',
                                  border: 'none',
                                  backgroundColor: 'transparent',
                                  color: '#ff9800',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  textAlign: 'left',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  borderBottom: '1px solid #f0f0f0',
                                  fontWeight: '500'
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = '#fff3e0'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                              >
                                <FiTrash2 size={12} />
                                Nur aktuelle Version behalten
                              </button>
                            )}
                            
                            {/* Kompletten Plan mit allen Versionen löschen - nur bei mehreren Versionen */}
                            {(planVersions[plan.base_name]?.length > 1 || plan.version > 1) && (
                              <button
                                onClick={() => handleDeleteAllVersions(plan.base_name)}
                                style={{
                                  width: '100%',
                                  padding: '8px 12px',
                                  border: 'none',
                                  backgroundColor: 'transparent',
                                  color: '#d32f2f',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  textAlign: 'left',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  fontWeight: 'bold'
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = '#ffebee'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                title="Löscht den kompletten Plan mit ALLEN Versionen unwiderruflich"
                              >
                                <FiTrash2 size={12} />
                                Kompletten Plan löschen
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {/* Erweiterte Versionen */}
                {isExpanded && versions.length > 0 && (
                  <div style={{
                    borderTop: '1px solid #f0f0f0',
                    backgroundColor: '#fafafa',
                  }}>
                    {versions.map(version => {
                      const isVersionLoaded = currentLoadedPlanId === version.id;
                      return (
                        <div key={version.id} style={{
                          padding: '6px 16px',
                          borderBottom: version.id === versions[versions.length - 1].id ? 'none' : '1px solid #f0f0f0',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          backgroundColor: isVersionLoaded ? 'rgba(33, 150, 243, 0.05)' : 'transparent',
                          borderLeft: isVersionLoaded ? '3px solid #2196f3' : 'none',
                          marginLeft: isVersionLoaded ? '-3px' : '0'
                        }}>
                          <div style={{ flex: 1 }}>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              cursor: 'pointer'
                            }}
                            onClick={() => {
                              onLoadFromDatabase(version.id);
                            }}
                            title="Klicken zum Laden"
                            >
                              <div style={{
                                fontSize: '12px',
                                color: version.id === plan.id ? '#4caf50' : '#666',
                                fontWeight: version.id === plan.id ? '500' : 'normal'
                              }}>
                                Version {version.version}
                                {version.id === plan.id && ' (aktuell)'}
                              </div>
                            </div>
                            <div style={{
                              fontSize: '10px',
                              color: '#999'
                            }}>
                              {new Date(version.updated_at).toLocaleDateString('de-DE', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                          </div>
                          {showEditButtons && (
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <button
                                onClick={() => setPublishDialog({ 
                                  open: true, 
                                  planId: version.id, 
                                  planName: `${version.name} (v${version.version})` 
                                })}
                                style={{
                                  padding: '2px',
                                  border: 'none',
                                  backgroundColor: publicationStatus[version.id] ? '#2196f3' : '#a3a3a3ff',
                                  color: 'white',
                                  borderRadius: '3px',
                                  cursor: 'pointer',
                                  fontSize: '10px'
                                }}
                                title={publicationStatus[version.id] ? "Version ist veröffentlicht - verwalten" : "Diese Version veröffentlichen"}
                              >
                                <FiShare2 size={10} />
                              </button>
                              <button
                                onClick={() => handleStudentReleaseClick(version.id, studentReleaseStatus[version.id])}
                                style={{
                                  padding: '2px',
                                  border: 'none',
                                  backgroundColor: studentReleaseStatus[version.id] ? '#4caf50' : '#a3a3a3ff',
                                  color: 'white',
                                  borderRadius: '3px',
                                  cursor: 'pointer',
                                  fontSize: '10px'
                                }}
                                title={studentReleaseStatus[version.id] ? "Version ist für Lernende freigegeben (Klick zum Sperren)" : "Diese Version für Lernende freigeben"}
                              >
                                <FiUsers size={10} />
                              </button>
                              {version.id !== plan.id && (
                                <button
                                  onClick={() => handleDeletePlan(version.id, `${version.name} (v${version.version})`)}
                                  style={{
                                    padding: '2px',
                                    border: 'none',
                                    backgroundColor: '#f44336',
                                    color: 'white',
                                    borderRadius: '3px',
                                    cursor: 'pointer',
                                    fontSize: '10px'
                                  }}
                                  title="Version löschen"
                                >
                                  <FiTrash2 size={10} />
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      
      {/* Publish Dialog */}
      <PublishDialog
        isOpen={publishDialog.open}
        onClose={() => setPublishDialog({ open: false, planId: null, planName: null })}
        planId={publishDialog.planId}
        planName={publishDialog.planName}
        user={user}
        onSuccess={() => {
          // Finde den baseName für die aktualisierte Plan-ID
          const updatedPlan = plans.find(p => p.id === publishDialog.planId) ||
            Object.values(planVersions).flat().find(v => v.id === publishDialog.planId);
          
          if (updatedPlan) {
            // Lade Status für alle Versionen dieses Plans neu
            loadAllVersionsPublicationStatus(updatedPlan.base_name || updatedPlan.name);
          }
          
          // Fallback: Lade kompletten Status neu
          loadPublicationStatus();
          
          // Aktualisiere auch die veröffentlichten Pläne
          if (window.refreshPublishedPlans) {
            window.refreshPublishedPlans();
          }
        }}
      />
    </div>
  );
};

export default MeinePlaene;
